import React, { useState, useRef } from 'react';

import config from 'config';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import styled, { css } from 'styled-components';

import {
  useAccountActions,
  useMembership,
  updateCustomerDetail,
} from '@techstyle/react-accounts';
import { ImageAsset, useAsset } from '@techstyle/react-assets';
import { TextLink } from '@techstyle/react-components';
import { FormattedMessage } from '@techstyle/react-intl';
import { useDomain, updateSessionDetail } from '@techstyle/redux-core';

import { trackEmailOptInInteractions } from '../../actions/email';
import { desktop } from '../../styles';
import { BasicButton } from '../../styles/button';
import { BasicModal } from '../../styles/modal';
import { Paragraph } from '../../styles/paragraph';
import { formatEmailPreferencesPayload } from '../../utils/formatEmailPreferences';
import { useEmailPreferencesWithVars } from '../../utils/useEmailPreferencesWithVars';
import { Component as CloseButton } from '../CloseButton';

const emailOptInMarketingList = config.get('public.sailthru.emailOptIn');

const BackgroundImage = styled(ImageAsset)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${props => props.theme.spacing.large}px
    ${props => props.theme.spacing.medium}px;
`;
const Content = styled.div`
  text-align: center;
`;

const dialogStyle = css`
  ${desktop`
    width: 476px;
  `}
  border-radius: 5px;
  margin: 0px ${props => props.theme.spacing.small}px;
`;

const NotificationModal = styled(BasicModal)``;

const StyleCloseButton = styled(CloseButton)`
  color: ${props => props.theme.colors.white};
`;

const SubscribeButton = styled(BasicButton)`
  margin: ${props => props.theme.spacing.moderate}px 0;
  height: 44px;
  width: 100%;
`;

const OptOutMessage = styled(Paragraph)`
  color: ${props => props.theme.colors.white};
  font-weight: 100;
`;

const PrivacyNotice = styled.div`
  font-weight: 100;
  margin-top: ${props => props.theme.spacing.tiny}px;
  margin-bottom: ${props => props.theme.spacing.medium}px;
  ${TextLink} {
    color: ${props => props.theme.colors.white};
    text-decoration: underline;
    &::before {
      transition: none;
      background-color: transparent;
    }
  }
`;

const NoThankYouButton = styled.button`
  font-size: 16px;
  font-weight: 100;
  cursor: pointer;
  color: ${props => props.theme.colors.white};
  text-decoration: underline;
`;

const Header = styled.div`
  font-weight: 500;
  font-size: 24px;
  color: ${props => props.theme.colors.white};
`;

const SuccessMessage = styled.div`
  font-size: 18px;
  font-weight: 100;
  color: ${props => props.theme.colors.white};
  margin-top: 4px;
  margin-bottom: ${props => props.theme.spacing.medium}px;
`;

const PromoHeader = styled.div`
  font-size: 14px;
  text-align: center;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: ${props => props.theme.colors.white};
  margin-bottom: 12px;
`;

const PromoCodeContainer = styled.div`
  ${desktop`
      padding: 0 60px;
  `}
`;

const PromoBorderBox = styled.div`
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 2px;
  border: 1px solid ${props => props.theme.colors.white};
`;

const CopyButton = styled(BasicButton)`
  height: 100%;
  min-width: auto;
`;

const PromoCode = styled.input`
  width: 100%;
  height: 100%;
  border: none;
  font-size: 20px;
  text-align: center;
  background-color: transparent;
  color: ${props => props.theme.colors.white};
`;

const EmailNotificationModal = ({ isOpen, onRequestClose }) => {
  const dispatch = useDispatch();
  const [isDisabled, setDisabled] = useState(false);
  const [isPromoCopied, setIsPromoCopied] = useState(false);
  const [shouldShowThankYouMessage, setShowThankYouMessage] = useState(false);
  const { updateEmailPreferences } = useAccountActions();
  const emailPreferences = useEmailPreferencesWithVars();
  const membership = useMembership();
  const { tld } = useDomain();
  const promoRef = useRef();
  const assetContainerEmailNotification = useAsset('email_notification', {
    multi: true,
  });
  const { data } = assetContainerEmailNotification;
  const assetsEmailNotification = data ? data.assets : [];

  const modalBgAsset = assetsEmailNotification.find(asset =>
    asset.options.customVars
      ? asset.options.customVars.type === 'modal_bg'
      : false
  );
  const modalImageAsset = assetsEmailNotification.find(asset =>
    asset.options.customVars
      ? asset.options.customVars.type === 'modal_image'
      : false
  );
  const promoCodeAsset = assetsEmailNotification.find(asset =>
    asset.options.customVars
      ? asset.options.customVars.type === 'promo_code'
      : false
  );

  let lists = [
    membership.isVip
      ? emailOptInMarketingList.vip[tld]
      : emailOptInMarketingList.lead[tld],
  ];
  if (emailPreferences.lists && emailPreferences.lists.length) {
    lists = [...lists, ...emailPreferences.lists];
  }

  const handleDeclineOffer = () => {
    onRequestClose();
    dispatch(trackEmailOptInInteractions('No Thanks'));
    dispatch(
      updateSessionDetail({ name: 'email_opt_in_decline_offer', value: true })
    );
  };

  const handleSubscribe = async () => {
    setDisabled(true);
    try {
      await updateEmailPreferences({
        ...formatEmailPreferencesPayload('always'),
        lists,
      });
      setShowThankYouMessage(true);
      dispatch(trackEmailOptInInteractions('Subscribe'));
      dispatch(
        updateCustomerDetail({ name: 'email_opt_in_subscribe', value: '' })
      );
    } catch (err) {
      setDisabled(false);
    }
  };

  const handleCopyPromo = event => {
    promoRef.current.select();
    document.execCommand('copy');
    setIsPromoCopied(true);
    event.target.focus();
  };

  const handleClickOnClose = () => {
    onRequestClose();
    dispatch(trackEmailOptInInteractions('Close'));
  };

  const renderSubscribeContent = modalImageAsset => (
    <Content>
      <ImageAsset asset={modalImageAsset} />
      <SubscribeButton onClick={handleSubscribe} disabled={isDisabled}>
        <FormattedMessage
          id="site_home.newsletter_subscribe"
          defaultMessage="Subscribe"
        />
      </SubscribeButton>
      <OptOutMessage id="email-subscribe-notification-modal">
        <FormattedMessage
          id="site_home.opt_out_anytime"
          defaultMessage="You can opt-out anytime"
        />
      </OptOutMessage>
      <PrivacyNotice>
        <TextLink href="/privacypolicy">
          <FormattedMessage
            defaultMessage="Privacy Policy"
            id="global_checkout.privacy_policy"
          />
        </TextLink>
      </PrivacyNotice>
      <NoThankYouButton onClick={handleDeclineOffer}>
        <FormattedMessage
          defaultMessage="No, thank you. I don't want this offer"
          id="site_home.no_thank_you_offer"
        />
      </NoThankYouButton>
    </Content>
  );

  const renderThankYouContent = promoCodeAsset => (
    <Content>
      <Header>
        <FormattedMessage
          defaultMessage="Thank you!"
          id="site_home.thank_you"
        />
      </Header>
      <SuccessMessage id="email-success-notification-modal">
        <FormattedMessage
          defaultMessage="Your email has been added to the list."
          id="site_home.email_added_list"
        />
      </SuccessMessage>
      {promoCodeAsset && promoCodeAsset.options.customVars.value ? (
        <>
          <PromoHeader>
            <FormattedMessage
              defaultMessage="Your 20% off code"
              id="site_home.email_opt_promo_header"
            />
            :
          </PromoHeader>
          <PromoCodeContainer>
            <PromoBorderBox>
              <CopyButton white onClick={handleCopyPromo}>
                {isPromoCopied ? (
                  <FormattedMessage
                    defaultMessage="Copied"
                    id="site_home.email_opt_copied_promo"
                  />
                ) : (
                  <FormattedMessage
                    defaultMessage="Copy"
                    id="site_home.email_opt_copy_promo"
                  />
                )}
              </CopyButton>
              <PromoCode
                ref={promoRef}
                value={promoCodeAsset.options.customVars.value}
              />
            </PromoBorderBox>
          </PromoCodeContainer>
        </>
      ) : null}
    </Content>
  );

  return (
    <NotificationModal
      isOpen={isOpen}
      onExit={handleClickOnClose}
      closeButton={<StyleCloseButton iconSize={21} />}
      dialogStyle={dialogStyle}
      titleId="email-notification-modal"
    >
      {modalBgAsset ? (
        <BackgroundImage asset={modalBgAsset} background>
          {shouldShowThankYouMessage
            ? renderThankYouContent(promoCodeAsset)
            : renderSubscribeContent(modalImageAsset)}
        </BackgroundImage>
      ) : null}
    </NotificationModal>
  );
};

EmailNotificationModal.propTypes = {
  isOpen: PropTypes.bool,
  onRequestClose: PropTypes.func,
};

export default EmailNotificationModal;
